/**
 * Helpers de statut de publication d'un bien.
 *
 * Source de vérité unique pour :
 * - Dashboard CLEVEL (predicate `published-stale-active` dans dashboardViews.ts)
 * - Bannière "Modifs non poussées" sur BienDetailPage
 * - Pastille globale dans la colonne Portails de SuiviPublicationPage
 * - Garde-fou de navigation (useBlocker) sur BienDetailPage
 *
 * Les 3 sources s'appuient sur le même helper pour qu'aucune dérive ne soit
 * possible entre le compteur dashboard, l'alerte fiche et le point orange Suivi.
 */

import type { Bien } from "@/types/bien";

/**
 * 3 états mutuellement exclusifs pour la pastille globale :
 * - "non-publie" : statut Skyline = Brouillon ou Retiré
 * - "publie"    : Publié + last_published_at non NULL + updated_at <= last_published_at
 * - "stale"     : Publié MAIS (last_published_at NULL OU updated_at > last_published_at)
 *
 * Décision produit 2026-05-07 (option B) : tant que le user n'a pas cliqué
 * explicitement sur "Publier" (qui bump `last_published_at = now()`), la
 * pastille reste orange — même si `statutPublication = "Publié"`. Le passage
 * dans le dropdown rend le bien visible côté API vendor (Site Web), mais la
 * "validation explicite" est un acte séparé.
 */
export type PublicationStatus = "non-publie" | "publie" | "stale";

/**
 * Vrai si le bien est en statut Publié ET au moins une des conditions :
 *  - jamais cliqué sur Publier (`last_published_at IS NULL`)
 *  - un champ CONTENU PORTAIL a été modifié depuis le dernier clic Publier
 *    (`content_updated_at > last_published_at`)
 *
 * ⚠️ On compare `content_updated_at`, PAS `updated_at`. `updated_at` (trigger
 * migration 009) bouge à CHAQUE sauvegarde de n'importe quel champ — donc une
 * édition SRS / État des lieux / Locataires / contrôles de publication / PEB
 * opérationnel / champs internes marquait le bien "à pousser" à tort. La
 * colonne `content_updated_at` (trigger migration 066) ne bouge QUE quand un
 * champ réellement envoyé au portail change (adresse, prix, surfaces, chambres,
 * photos, titres/descriptions, certificat PEB, …). Fallback sur `updated_at`
 * pour les rares chemins de données n'ayant pas encore la colonne.
 *
 * Cf. RPC `mark_bien_as_published` (migration 045) qui bump
 * `last_published_at = now()` côté frontend.
 */
export function hasUnpublishedChanges(
  bien: Pick<Bien, "statutPublication" | "updated_at" | "last_published_at" | "content_updated_at">,
): boolean {
  if (bien.statutPublication !== "Publié") return false;
  // Jamais cliqué Publier → considéré comme "à pousser"
  if (!bien.last_published_at) return true;
  const contentTs = bien.content_updated_at ?? bien.updated_at;
  if (!contentTs) return false;
  return new Date(contentTs).getTime() > new Date(bien.last_published_at).getTime();
}

/**
 * Statut global affiché dans la pastille (fiche + Suivi Publication).
 */
export function getPublicationStatus(
  bien: Pick<Bien, "statutPublication" | "updated_at" | "last_published_at" | "content_updated_at">,
): PublicationStatus {
  if (bien.statutPublication !== "Publié") return "non-publie";
  return hasUnpublishedChanges(bien) ? "stale" : "publie";
}
